import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  inject
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { Estado } from 'app/core/dtos/sai/core/estado';
import { Municipio } from 'app/core/dtos/sai/core/municipio';
import { Orgao } from 'app/core/dtos/sai/core/orgao';
import { AuthService } from 'app/core/services/auth/auth.service';
import { EstadoService } from 'app/core/services/sai/core/estado/estado-service.service';
import { MunicipioService } from 'app/core/services/sai/core/municipio/municipio-service.service';
import { OrgaoService } from 'app/core/services/sai/core/orgao/orgao-service.service';
import { filtrarOpcoes } from 'app/shared/utils/busca-select.util';

interface TrocarClienteDialogData {
  data: unknown;
  action: string;
}

@Component({
  selector: 'app-trocar-cliente',
  standalone: false,
  templateUrl: './trocar-cliente.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './trocar-cliente.component.css'
})
export class TrocarClienteComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly filtrarOpcoes = filtrarOpcoes;

  buscaEstado = '';
  buscaMunicipio = '';
  buscaOrgao = '';

  estados: Estado[] = [];
  municipios: Municipio[] = [];
  orgaos: Orgao[] = [];
  form!: UntypedFormGroup;

  carregandoEstados = false;
  carregandoMunicipios = false;
  carregandoOrgaos = false;
  salvando = false;
  mensagemErro = '';

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly estadoService: EstadoService,
    private readonly municipioService: MunicipioService,
    private readonly orgaoService: OrgaoService,
    private readonly authService: AuthService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly dialogRef: MatDialogRef<TrocarClienteComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly dialogData: TrocarClienteDialogData,
  ) { }

  ngOnInit(): void {
    this.criarFormulario();
    this.acompanharAlteracoes();
    this.carregarEstados();
  }

  get f() {
    return this.form.controls;
  }

  private criarFormulario(): void {
    this.form = this.formBuilder.group({
      estadoId: [null, Validators.required],
      municipioId: [{ value: null, disabled: true }, Validators.required],
      orgaoId: [{ value: null, disabled: true }, Validators.required]
    });
  }

  private acompanharAlteracoes(): void {
    this.form.get('estadoId')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((estadoId: number | null) => {
        this.limparMunicipiosEOrgaos();

        if (estadoId) {
          this.carregarMunicipios(estadoId);
        }
      });

    this.form.get('municipioId')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((municipioId: number | null) => {
        this.limparOrgaos();

        if (municipioId) {
          this.carregarOrgaos(municipioId);
        }
      });
  }

  private carregarEstados(): void {
    this.carregandoEstados = true;
    this.mensagemErro = '';
    this.form.get('estadoId')?.disable({ emitEvent: false });

    this.estadoService.getEstadosWithOrgao()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.carregandoEstados = false;
          this.form.get('estadoId')?.enable({ emitEvent: false });
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.mensagemErro = response.message || 'Não foi possível carregar os estados.';
            return;
          }

          this.estados = [...(response.data ?? [])]
            .sort((a, b) => a.desNomeEstadoEst.localeCompare(b.desNomeEstadoEst));
        },
        error: (error) => this.tratarErroNaoHttp(error, 'Não foi possível carregar os estados.')
      });
  }

  private carregarMunicipios(estadoId: number): void {
    this.carregandoMunicipios = true;
    this.mensagemErro = '';

    this.municipioService.getMunicipioByEstado(estadoId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.carregandoMunicipios = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe({
        next: (response) => {
          if (this.form.get('estadoId')?.value !== estadoId) return;

          if (!response.success) {
            this.mensagemErro = response.message || 'Não foi possível carregar os municípios.';
            return;
          }

          this.municipios = [...(response.data ?? [])]
            .sort((a, b) => a.desNomeMun.localeCompare(b.desNomeMun));
          this.form.get('municipioId')?.enable({ emitEvent: false });
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          if (this.form.get('estadoId')?.value === estadoId) {
            this.tratarErroNaoHttp(error, 'Não foi possível carregar os municípios.');
          }
        }
      });
  }

  private carregarOrgaos(municipioId: number): void {
    this.carregandoOrgaos = true;
    this.mensagemErro = '';

    this.orgaoService.getOrgaoByMunicipio(municipioId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.carregandoOrgaos = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe({
        next: (response) => {
          if (this.form.get('municipioId')?.value !== municipioId) return;

          if (!response.success) {
            this.mensagemErro = response.message || 'Não foi possível carregar os órgãos.';
            return;
          }

          this.orgaos = [...(response.data ?? [])]
            .sort((a, b) => a.desNomeOrg.localeCompare(b.desNomeOrg));
          this.form.get('orgaoId')?.enable({ emitEvent: false });
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          if (this.form.get('municipioId')?.value === municipioId) {
            this.tratarErroNaoHttp(error, 'Não foi possível carregar os órgãos.');
          }
        }
      });
  }

  private limparMunicipiosEOrgaos(): void {
    this.municipios = [];
    this.form.get('municipioId')?.reset(null, { emitEvent: false });
    this.form.get('municipioId')?.disable({ emitEvent: false });
    this.limparOrgaos();
  }

  private limparOrgaos(): void {
    this.orgaos = [];
    this.form.get('orgaoId')?.reset(null, { emitEvent: false });
    this.form.get('orgaoId')?.disable({ emitEvent: false });
  }

  onSubmit(): void {
    if (
      this.form.invalid
      || this.carregandoEstados
      || this.carregandoMunicipios
      || this.carregandoOrgaos
      || this.salvando
    ) {
      this.form.markAllAsTouched();
      return;
    }

    const orgaoId = Number(this.form.getRawValue().orgaoId);
    this.salvando = true;
    this.mensagemErro = '';
    this.form.disable({ emitEvent: false });

    this.authService.changeOrgao(orgaoId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.salvando = false;
          this.form.enable({ emitEvent: false });
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe({
        next: (user) => {
          this.dialogRef.close({
            success: true,
            message: `Cliente alterado para ${user.orgao}.`,
            user
          });
        },
        error: (error) => this.tratarErroNaoHttp(error, 'Não foi possível trocar o cliente.')
      });
  }

  private tratarErroNaoHttp(error: unknown, mensagemPadrao: string): void {
    if (error instanceof HttpErrorResponse) return;

    this.mensagemErro = error instanceof Error && error.message
      ? error.message
      : mensagemPadrao;
  }
}
